/**
 * Theme catalog for the Gallery Creator.
 * Each theme renders a list of media items. Click handler opens the lightbox.
 */
import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

// Picks the right URL for a given rendering context.
// - "thumb"   -> grids / small tiles (480px WebP)
// - "display" -> hero / fullscreen slots (1920px WebP)
// - "full"    -> lightbox / zoomed view (untouched original)
const srcFor = (m, size) => {
  if (!m) return '';
  if (size === 'thumb') return m.thumb_url || m.display_url || m.url;
  if (size === 'full') return m.url || m.display_url || m.thumb_url;
  return m.display_url || m.url || m.thumb_url;
};

const Img = ({ m, className = '', onClick, autoPlay = true, size = 'display' }) => {
  if (m?.media_type === 'video') {
    return (
      <video
        src={m.url}
        poster={m.thumb_url || m.display_url || undefined}
        className={className}
        onClick={onClick}
        muted
        loop
        playsInline
        autoPlay={autoPlay}
        preload="metadata"
      />
    );
  }
  return (
    <img
      src={srcFor(m, size)}
      alt={m?.alt_text || m?.title || ''}
      className={className}
      onClick={onClick}
      loading="lazy"
      decoding="async"
    />
  );
};

// AnimatePresenceMedia — cross-fade wrapper for the Slideshow theme that works for images AND videos.
const AnimatePresenceMedia = ({ m, onClick }) => (
  <motion.div
    initial={{ opacity: 0, scale: 1.04 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 1.2, ease: 'easeInOut' }}
    className="absolute inset-0 w-full h-full"
  >
    <Img m={m} className="absolute inset-0 w-full h-full object-cover" onClick={onClick} />
  </motion.div>
);

export const GridTheme = ({ media, columns = 4, gap = 12, onOpen, showCaptions }) => (
  <div
    className="grid"
    style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap }}
  >
    {media.map((m, i) => (
      <figure key={m.id} className="group relative overflow-hidden rounded-xl bg-stone-900 cursor-pointer" onClick={() => onOpen(i)}>
        <Img m={m} className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-110" />
        {showCaptions && (m.title || m.caption) && (
          <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent text-white p-3 text-sm opacity-0 group-hover:opacity-100 transition-opacity">
            {m.title && <div className="font-semibold">{m.title}</div>}
          </figcaption>
        )}
      </figure>
    ))}
  </div>
);

export const MasonryTheme = ({ media, columns = 4, gap = 12, onOpen, showCaptions }) => (
  <div style={{ columnCount: columns, columnGap: gap }}>
    {media.map((m, i) => (
      <figure key={m.id} className="mb-3 break-inside-avoid overflow-hidden rounded-xl bg-stone-900 cursor-pointer group" onClick={() => onOpen(i)}>
        <Img m={m} className="w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {showCaptions && m.title && (
          <figcaption className="p-2 text-sm text-white/90 bg-stone-900">{m.title}</figcaption>
        )}
      </figure>
    ))}
  </div>
);

export const PolaroidTheme = ({ media, onOpen, showCaptions }) => (
  <div className="flex flex-wrap gap-8 justify-center">
    {media.map((m, i) => {
      const rotate = ((i % 7) - 3) * 2; // -6..6 deg
      return (
        <motion.figure
          key={m.id}
          whileHover={{ scale: 1.05, rotate: 0, zIndex: 10 }}
          style={{ rotate: `${rotate}deg` }}
          className="bg-white p-3 pb-10 shadow-2xl cursor-pointer relative"
          onClick={() => onOpen(i)}
        >
          <Img m={m} className="w-56 h-56 object-cover" />
          {showCaptions && m.title && (
            <figcaption className="absolute bottom-2 left-0 right-0 text-center text-stone-900 text-sm font-handwriting px-2">{m.title}</figcaption>
          )}
        </motion.figure>
      );
    })}
  </div>
);

export const SlideshowTheme = ({ media, autoplay_ms = 4000, onOpen, showCaptions }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!autoplay_ms) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % media.length), autoplay_ms);
    return () => clearInterval(t);
  }, [autoplay_ms, media.length]);
  if (!media.length) return null;
  const m = media[idx];
  return (
    <div className="relative w-full aspect-[16/9] overflow-hidden rounded-2xl bg-black">
      <AnimatePresenceMedia key={m.id} m={m} onClick={() => onOpen(idx)} />
      {showCaptions && (m.title || m.caption) && (
        <div className="absolute bottom-6 left-6 right-6 bg-black/50 backdrop-blur text-white p-4 rounded-xl">
          {m.title && <div className="font-bold text-xl">{m.title}</div>}
          {m.caption && <div className="text-sm text-white/80" dangerouslySetInnerHTML={{ __html: m.caption }} />}
        </div>
      )}
      <div className="absolute bottom-3 right-3 flex gap-1">
        {media.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            className={`w-2 h-2 rounded-full ${i === idx ? 'bg-primary' : 'bg-white/40'}`}
          />
        ))}
      </div>
    </div>
  );
};

export const KenburnsTheme = (props) => <SlideshowTheme {...props} />;

export const CarouselTheme = ({ media, gap = 12, onOpen, showCaptions }) => {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 400, behavior: 'smooth' });
  return (
    <div className="relative">
      <button onClick={() => scroll(-1)} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-black/70 text-white rounded-full">‹</button>
      <button onClick={() => scroll(1)} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-black/70 text-white rounded-full">›</button>
      <div ref={ref} className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth" style={{ gap }}>
        {media.map((m, i) => (
          <figure key={m.id} className="min-w-[300px] md:min-w-[400px] snap-start overflow-hidden rounded-xl bg-stone-900 cursor-pointer group" onClick={() => onOpen(i)}>
            <Img m={m} className="w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-105" />
            {showCaptions && m.title && <figcaption className="p-2 text-white/90 text-sm">{m.title}</figcaption>}
          </figure>
        ))}
      </div>
    </div>
  );
};

export const MosaicTheme = ({ media, gap = 12, onOpen }) => (
  <div className="grid grid-cols-6 auto-rows-[120px]" style={{ gap }}>
    {media.map((m, i) => {
      const patterns = [
        'col-span-2 row-span-2', 'col-span-2 row-span-1', 'col-span-1 row-span-2',
        'col-span-3 row-span-1', 'col-span-1 row-span-1', 'col-span-2 row-span-2',
      ];
      const p = patterns[i % patterns.length];
      return (
        <figure key={m.id} className={`${p} overflow-hidden rounded-xl bg-stone-900 cursor-pointer group`} onClick={() => onOpen(i)}>
          <Img m={m} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </figure>
      );
    })}
  </div>
);

export const JustifiedTheme = ({ media, gap = 12, onOpen }) => (
  <div className="flex flex-wrap" style={{ gap }}>
    {media.map((m, i) => (
      <figure key={m.id} className="flex-grow basis-[300px] max-w-[500px] overflow-hidden rounded-xl bg-stone-900 cursor-pointer group" onClick={() => onOpen(i)}>
        <Img m={m} className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105" />
      </figure>
    ))}
  </div>
);

export const TimelineTheme = ({ media, onOpen, showCaptions }) => (
  <div className="relative pl-10 border-l-2 border-primary/60 space-y-6">
    {media.map((m, i) => (
      <div key={m.id} className="relative">
        <div className="absolute -left-[46px] top-4 w-4 h-4 rounded-full bg-primary ring-4 ring-primary/20" />
        <figure className="rounded-xl overflow-hidden bg-stone-900 cursor-pointer group max-w-xl" onClick={() => onOpen(i)}>
          <Img m={m} className="w-full h-80 object-cover" />
          {showCaptions && (m.title || m.caption) && (
            <figcaption className="p-3 text-white/90 text-sm">
              {m.title && <div className="font-semibold">{m.title}</div>}
              {m.caption && <div className="text-white/70" dangerouslySetInnerHTML={{ __html: m.caption }} />}
            </figcaption>
          )}
        </figure>
      </div>
    ))}
  </div>
);

export const FilmstripTheme = ({ media, onOpen }) => (
  <div className="flex overflow-x-auto gap-2 py-4 bg-stone-900/60 rounded-xl px-4">
    {media.map((m, i) => (
      <figure key={m.id} className="min-w-[180px] overflow-hidden rounded-lg bg-stone-800 cursor-pointer border-2 border-stone-700 hover:border-primary" onClick={() => onOpen(i)}>
        <Img m={m} className="w-full h-32 object-cover" />
      </figure>
    ))}
  </div>
);

export const SpotlightTheme = ({ media, onOpen, showCaptions }) => {
  const [idx, setIdx] = useState(0);
  if (!media.length) return null;
  const m = media[idx];
  return (
    <div>
      <div className="relative aspect-[16/9] overflow-hidden rounded-2xl bg-black">
        <Img m={m} className="w-full h-full object-cover cursor-zoom-in" onClick={() => onOpen(idx)} />
        {showCaptions && m.title && (
          <div className="absolute bottom-4 left-6 right-6 text-white">
            <div className="text-2xl font-bold">{m.title}</div>
            {m.caption && <div className="text-sm text-white/80">{m.caption}</div>}
          </div>
        )}
      </div>
      <div className="flex gap-2 mt-3 overflow-x-auto">
        {media.map((t, i) => (
          <button key={t.id} onClick={() => setIdx(i)} className={`min-w-[90px] h-16 rounded overflow-hidden border-2 relative ${i === idx ? 'border-primary' : 'border-stone-700'}`}>
            <img loading="lazy" decoding="async" src={t.thumb_url || t.url} alt="" className="w-full h-full object-cover" />
            {t.media_type === 'video' && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <div className="w-0 h-0 border-y-[6px] border-y-transparent border-l-[9px] border-l-white" />
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export const ScrapbookTheme = (props) => <PolaroidTheme {...props} />;

export const Cube3DTheme = ({ media, onOpen }) => {
  const [angle, setAngle] = useState(0);
  const faces = media.slice(0, 6);
  if (!faces.length) return null;
  return (
    <div className="flex flex-col items-center" style={{ perspective: 1200 }}>
      <div
        className="relative"
        style={{ width: 280, height: 280, transformStyle: 'preserve-3d', transform: `rotateY(${angle}deg) rotateX(${angle / 2}deg)`, transition: 'transform 0.8s' }}
      >
        {faces.map((m, i) => {
          const transforms = [
            'rotateY(0deg) translateZ(140px)',
            'rotateY(90deg) translateZ(140px)',
            'rotateY(180deg) translateZ(140px)',
            'rotateY(-90deg) translateZ(140px)',
            'rotateX(90deg) translateZ(140px)',
            'rotateX(-90deg) translateZ(140px)',
          ];
          return (
            <div
              key={m.id}
              onClick={() => onOpen(i)}
              className="absolute top-0 left-0 w-[280px] h-[280px] overflow-hidden cursor-pointer"
              style={{ transform: transforms[i], backfaceVisibility: 'hidden' }}
            >
              <Img m={m} className="w-full h-full object-cover" />
            </div>
          );
        })}
      </div>
      <div className="mt-6 flex gap-2">
        <button onClick={() => setAngle((a) => a - 90)} className="px-4 py-2 bg-stone-800 text-white rounded">◀ Rotate</button>
        <button onClick={() => setAngle((a) => a + 90)} className="px-4 py-2 bg-stone-800 text-white rounded">Rotate ▶</button>
      </div>
    </div>
  );
};

export const CoverflowTheme = ({ media, onOpen }) => {
  const [idx, setIdx] = useState(0);
  if (!media.length) return null;
  return (
    <div className="relative h-[400px] flex items-center justify-center overflow-hidden" style={{ perspective: 1400 }}>
      {media.map((m, i) => {
        const offset = i - idx;
        const abs = Math.abs(offset);
        if (abs > 3) return null;
        return (
          <div
            key={m.id}
            onClick={() => (offset === 0 ? onOpen(i) : setIdx(i))}
            className="absolute cursor-pointer transition-transform duration-500"
            style={{
              transform: `translateX(${offset * 180}px) rotateY(${offset * -25}deg) translateZ(${-abs * 60}px)`,
              zIndex: 100 - abs,
              opacity: 1 - abs * 0.2,
            }}
          >
            <Img m={m} className="w-80 h-60 object-cover rounded-lg shadow-2xl" />
          </div>
        );
      })}
    </div>
  );
};

export const FlipcardsTheme = ({ media, columns = 4, gap = 12, onOpen, showCaptions }) => (
  <div className="grid" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap }}>
    {media.map((m, i) => (
      <div key={m.id} className="relative aspect-square cursor-pointer group" style={{ perspective: 800 }} onClick={() => onOpen(i)}>
        <div className="relative w-full h-full transition-transform duration-500 group-hover:[transform:rotateY(180deg)]" style={{ transformStyle: 'preserve-3d' }}>
          <div className="absolute inset-0 rounded-xl overflow-hidden" style={{ backfaceVisibility: 'hidden' }}>
            <Img m={m} className="w-full h-full object-cover" />
          </div>
          <div className="absolute inset-0 rounded-xl bg-stone-900 text-white p-4 flex flex-col items-center justify-center" style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
            <div className="font-bold text-center">{m.title || 'Click to view'}</div>
            {m.caption && <div className="text-sm text-white/70 text-center mt-2 line-clamp-4" dangerouslySetInnerHTML={{ __html: m.caption }} />}
          </div>
        </div>
      </div>
    ))}
  </div>
);

export const MarqueeTheme = ({ media, onOpen }) => (
  <div className="overflow-hidden">
    <motion.div
      className="flex gap-4"
      animate={{ x: ['0%', '-50%'] }}
      transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
    >
      {[...media, ...media].map((m, i) => (
        <div key={`${m.id}-${i}`} className="min-w-[260px] h-48 overflow-hidden rounded-xl cursor-pointer" onClick={() => onOpen(i % media.length)}>
          <Img m={m} className="w-full h-full object-cover" />
        </div>
      ))}
    </motion.div>
  </div>
);

export const KineticTheme = ({ media, onOpen }) => (
  <div className="flex flex-wrap gap-4 justify-center">
    {media.map((m, i) => (
      <motion.div
        key={m.id}
        whileHover={{ scale: 1.1, zIndex: 10 }}
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 3 + (i % 3), repeat: Infinity, delay: i * 0.1 }}
        className="w-40 h-40 overflow-hidden rounded-full border-4 border-stone-800 cursor-pointer"
        onClick={() => onOpen(i)}
      >
        <Img m={m} className="w-full h-full object-cover" />
      </motion.div>
    ))}
  </div>
);

export const ParallaxTheme = ({ media, onOpen, showCaptions }) => (
  <div className="space-y-24">
    {media.map((m, i) => (
      <div key={m.id} className="relative h-96 overflow-hidden rounded-2xl cursor-pointer" onClick={() => onOpen(i)}>
        <Img m={m} className="absolute inset-0 w-full h-full object-cover" />
        {showCaptions && m.title && (
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent text-white">
            <div className="text-3xl font-bold">{m.title}</div>
            {m.caption && <div dangerouslySetInnerHTML={{ __html: m.caption }} />}
          </div>
        )}
      </div>
    ))}
  </div>
);

export const ImmersiveTheme = (props) => <ParallaxTheme {...props} />;

// SkyscraperTheme — images arranged as lit windows of a tall building silhouette.
export const SkyscraperTheme = ({ media, onOpen, showCaptions }) => {
  // Pair images into floors of 2 windows each.
  const floors = [];
  for (let i = 0; i < media.length; i += 2) {
    floors.push([media[i], media[i + 1]].filter(Boolean));
  }
  return (
    <div className="flex justify-center">
      <div className="relative">
        {/* Antenna */}
        <div className="mx-auto w-1 h-10 bg-stone-700" />
        <div className="mx-auto w-3 h-3 -mt-1 rounded-full bg-amber-400 shadow-[0_0_14px_rgba(251,191,36,0.8)]" />
        {/* Roof */}
        <div className="mx-auto w-[280px] h-4 bg-stone-800 border-x-4 border-stone-900" />
        {/* Building body */}
        <div className="bg-stone-900 border-x-[14px] border-stone-800 shadow-2xl px-4 py-5 w-[280px]">
          <div className="space-y-3">
            {floors.map((row, fIdx) => (
              <div key={fIdx} className="grid grid-cols-2 gap-3">
                {row.map((m, i) => {
                  const absIdx = fIdx * 2 + i;
                  return (
                    <figure
                      key={m.id}
                      className="relative aspect-[3/4] overflow-hidden cursor-pointer group ring-2 ring-stone-800 bg-stone-950"
                      style={{ borderRadius: 2 }}
                      onClick={() => onOpen(absIdx)}
                    >
                      <Img
                        m={m}
                        className="w-full h-full object-cover brightness-50 group-hover:brightness-110 transition-all duration-300"
                      />
                      {/* Window cross frame */}
                      <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-stone-900/60" />
                        <div className="absolute top-1/2 left-0 right-0 h-px bg-stone-900/60" />
                      </div>
                      {/* Window glow on hover */}
                      <div className="absolute inset-0 bg-amber-300/0 group-hover:bg-amber-300/15 transition-colors" />
                      {showCaptions && m.title && (
                        <figcaption className="absolute bottom-0 inset-x-0 bg-black/80 text-white text-[10px] px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity truncate">
                          {m.title}
                        </figcaption>
                      )}
                    </figure>
                  );
                })}
                {/* Pad odd floor to keep window pairs symmetric */}
                {row.length === 1 && <div className="aspect-[3/4] bg-stone-950 ring-2 ring-stone-800" style={{ borderRadius: 2 }} />}
              </div>
            ))}
          </div>
          {/* Entrance */}
          <div className="mx-auto mt-4 w-16 h-10 bg-gradient-to-b from-amber-500/70 to-amber-900/40 rounded-t-xl ring-2 ring-stone-800" />
        </div>
        {/* Street/base */}
        <div className="mx-auto w-[340px] h-3 bg-stone-800 -mt-[1px]" />
        <div className="mx-auto w-[340px] h-1 bg-stone-700" />
      </div>
    </div>
  );
};

// VinylTheme — spinning vinyl records; image is the record label at the center.
export const VinylTheme = ({ media, columns = 4, gap = 24, onOpen, showCaptions }) => (
  <div
    className="grid place-items-center"
    style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap }}
  >
    {media.map((m, i) => (
      <div key={m.id} className="flex flex-col items-center">
        <motion.button
          type="button"
          onClick={() => onOpen(i)}
          whileHover={{ scale: 1.05 }}
          animate={{ rotate: 360 }}
          transition={{ rotate: { duration: 14, repeat: Infinity, ease: 'linear' } }}
          className="relative aspect-square w-full max-w-[240px] rounded-full overflow-hidden cursor-pointer shadow-2xl"
          style={{
            background:
              'radial-gradient(circle at center, #111 36%, #0a0a0a 36.5%, #1b1b1b 37%, #0a0a0a 42%, #1a1a1a 43%, #0a0a0a 55%, #1a1a1a 56%, #0a0a0a 65%, #1a1a1a 66%, #0a0a0a 78%, #141414 100%)',
          }}
        >
          {/* Shine arc */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'conic-gradient(from 210deg, rgba(255,255,255,0) 0deg, rgba(255,255,255,0.08) 20deg, rgba(255,255,255,0) 40deg)',
            }}
          />
          {/* Label */}
          <div className="absolute inset-[32%] rounded-full overflow-hidden ring-4 ring-stone-900">
            <Img m={m} className="w-full h-full object-cover" />
          </div>
          {/* Center hole */}
          <div className="absolute inset-[48%] rounded-full bg-stone-900 ring-1 ring-stone-700" />
        </motion.button>
        {showCaptions && (m.title || m.caption) && (
          <div className="mt-3 text-center">
            {m.title && <div className="text-white font-bold text-sm">{m.title}</div>}
            {m.caption && (
              <div
                className="text-stone-400 text-xs"
                dangerouslySetInnerHTML={{ __html: m.caption }}
              />
            )}
          </div>
        )}
      </div>
    ))}
  </div>
);

// Carousel3DTheme — tilted merry-go-round ring; auto-rotates, you see fronts, backs and sides.
export const Carousel3DTheme = ({ media, onOpen, showCaptions, autoplay_ms = 0 }) => {
  const [angle, setAngle] = useState(0);
  const [paused, setPaused] = useState(false);
  const [tilt, setTilt] = useState(-22); // viewing tilt in degrees (negative = tilting down toward viewer)
  const count = media.length;

  useEffect(() => {
    if (!count || paused) return undefined;
    const t = setInterval(() => setAngle((a) => a + 0.35), 40);
    return () => clearInterval(t);
  }, [count, paused]);

  if (!count) return null;
  const step = 360 / count;
  const radius = Math.max(280, Math.round(count * 60));

  return (
    <div className="w-full flex flex-col items-center select-none">
      <div
        className="relative"
        style={{ perspective: 1800, width: '100%', height: 560 }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Tilt stage — tilts the whole ring forward like a merry-go-round */}
        <div
          className="absolute left-1/2 top-1/2"
          style={{
            width: 320,
            height: 320,
            marginLeft: -160,
            marginTop: -160,
            transformStyle: 'preserve-3d',
            transform: `rotateX(${tilt}deg)`,
          }}
        >
          {/* Rotating ring */}
          <div
            className="absolute inset-0"
            style={{
              transformStyle: 'preserve-3d',
              transform: `rotateY(${angle}deg)`,
            }}
          >
            {media.map((m, i) => {
              const rot = i * step;
              return (
                <figure
                  key={m.id}
                  onClick={() => onOpen(i)}
                  className="absolute top-0 left-0 w-[320px] h-[320px] overflow-hidden rounded-xl bg-stone-900 cursor-pointer shadow-2xl ring-1 ring-white/10"
                  style={{
                    transform: `rotateY(${rot}deg) translateZ(${radius}px)`,
                    // No backfaceVisibility:hidden so reverse side shows (mirrored) — true merry-go-round look.
                  }}
                >
                  <Img m={m} className="w-full h-full object-cover" />
                  {showCaptions && m.title && (
                    <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent text-white p-3 text-sm">
                      <div className="font-semibold">{m.title}</div>
                    </figcaption>
                  )}
                </figure>
              );
            })}
            {/* Platform disc under the ring for merry-go-round feel */}
            <div
              className="absolute left-1/2 top-1/2 rounded-full"
              style={{
                width: radius * 2 + 320,
                height: radius * 2 + 320,
                marginLeft: -(radius + 160),
                marginTop: -(radius + 160),
                transform: 'rotateX(90deg) translateZ(-170px)',
                background:
                  'radial-gradient(circle at center, rgba(251,191,36,0.25), rgba(0,0,0,0.6) 70%, transparent 72%)',
              }}
            />
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-3 flex-wrap justify-center">
        <button onClick={() => setAngle((a) => a - step)} className="px-4 py-2 bg-stone-800 text-white rounded hover:bg-stone-700">◀ Prev</button>
        <button onClick={() => setPaused((p) => !p)} className="px-4 py-2 bg-primary text-primary-foreground rounded">{paused ? 'Play' : 'Pause'}</button>
        <button onClick={() => setAngle((a) => a + step)} className="px-4 py-2 bg-stone-800 text-white rounded hover:bg-stone-700">Next ▶</button>
        <label className="flex items-center gap-2 text-white/80 text-sm ml-2">
          Tilt
          <input
            type="range"
            min={-55}
            max={55}
            value={tilt}
            onChange={(e) => setTilt(Number(e.target.value))}
            className="accent-[color:var(--primary)]"
          />
        </label>
      </div>
    </div>
  );
};

// FilmReelTheme — rotating film reel with film strip unspooling across, images are frames.
export const FilmReelTheme = ({ media, onOpen, showCaptions }) => {
  if (!media.length) return null;
  const frames = media.length;
  const stripWidth = frames * 180 + 240;
  return (
    <div className="w-full">
      <div className="relative mx-auto" style={{ maxWidth: '100%' }}>
        {/* Reels */}
        <div className="flex items-center">
          {/* Left reel */}
          <Reel />
          {/* Film strip */}
          <div className="relative mx-2 flex-1 overflow-hidden rounded-sm" style={{ background: '#0a0a0a' }}>
            <motion.div
              className="relative flex gap-1 py-2"
              style={{ width: stripWidth * 2 }}
              animate={{ x: [0, -stripWidth] }}
              transition={{ duration: Math.max(20, frames * 3), repeat: Infinity, ease: 'linear' }}
            >
              {[...media, ...media].map((m, i) => (
                <div key={`${m.id}-${i}`} className="relative shrink-0">
                  {/* Top sprocket row */}
                  <div className="absolute -top-1 inset-x-0 h-3 flex justify-around items-center">
                    {[0,1,2,3].map((d) => (
                      <div key={d} className="w-2 h-2 bg-stone-950 rounded-[2px] ring-1 ring-stone-800" />
                    ))}
                  </div>
                  <figure
                    onClick={() => onOpen(i % frames)}
                    className="w-[180px] h-[130px] overflow-hidden cursor-pointer ring-1 ring-stone-700 bg-stone-900"
                    style={{ borderRadius: 2 }}
                  >
                    <Img m={m} className="w-full h-full object-cover" />
                    {showCaptions && m.title && (
                      <figcaption className="absolute inset-x-0 bottom-0 bg-black/80 text-white text-xs px-2 py-1 opacity-0 hover:opacity-100 transition-opacity">
                        {m.title}
                      </figcaption>
                    )}
                  </figure>
                  {/* Bottom sprocket row */}
                  <div className="absolute -bottom-1 inset-x-0 h-3 flex justify-around items-center">
                    {[0,1,2,3].map((d) => (
                      <div key={d} className="w-2 h-2 bg-stone-950 rounded-[2px] ring-1 ring-stone-800" />
                    ))}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
          {/* Right reel */}
          <Reel reversed />
        </div>
      </div>
    </div>
  );
};

const Reel = ({ reversed }) => (
  <motion.div
    className="relative shrink-0"
    style={{ width: 150, height: 150 }}
    animate={{ rotate: reversed ? -360 : 360 }}
    transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
  >
    <div className="absolute inset-0 rounded-full bg-stone-900 ring-4 ring-stone-800 shadow-2xl" />
    <div className="absolute inset-[12%] rounded-full bg-stone-950 ring-2 ring-stone-800" />
    {/* Spokes */}
    {[0, 60, 120].map((deg) => (
      <div
        key={deg}
        className="absolute left-1/2 top-1/2 w-1 h-[90%] bg-stone-700"
        style={{ transform: `translate(-50%, -50%) rotate(${deg}deg)`, borderRadius: 4 }}
      />
    ))}
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-stone-700 ring-2 ring-stone-800" />
  </motion.div>
);

// VideoWallTheme — cinematic grid: videos play silently on hover with a mute toggle; images stay still.
export const VideoWallTheme = ({ media, columns = 3, gap = 8, onOpen, showCaptions }) => {
  const [playingId, setPlayingId] = useState(null);
  const [mutedAll, setMutedAll] = useState(true);
  return (
    <div>
      <div className="flex justify-end mb-3">
        <button
          type="button"
          onClick={() => setMutedAll((m) => !m)}
          className="px-3 py-1.5 bg-stone-800 hover:bg-stone-700 rounded-md text-white text-xs font-semibold inline-flex items-center gap-2"
        >
          {mutedAll ? '🔇 Unmute hover' : '🔊 Mute hover'}
        </button>
      </div>
      <div
        className="grid"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap }}
      >
        {media.map((m, i) => {
          const isVideo = m.media_type === 'video';
          const playing = playingId === m.id;
          return (
            <figure
              key={m.id}
              className="relative overflow-hidden rounded-lg bg-black cursor-pointer group aspect-video ring-1 ring-stone-800"
              onMouseEnter={() => isVideo && setPlayingId(m.id)}
              onMouseLeave={() => isVideo && setPlayingId((pid) => (pid === m.id ? null : pid))}
              onClick={() => onOpen(i)}
            >
              {isVideo ? (
                <video
                  src={m.url}
                  poster={m.thumb_url || undefined}
                  className="w-full h-full object-cover"
                  muted={mutedAll}
                  loop
                  playsInline
                  autoPlay={playing}
                  preload="metadata"
                />
              ) : (
                <Img m={m} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              )}
              {/* Play glyph for videos that are not yet playing */}
              {isVideo && !playing && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/35 transition-opacity group-hover:opacity-0">
                  <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-xl">
                    <div className="w-0 h-0 border-y-[10px] border-y-transparent border-l-[16px] border-l-black ml-1" />
                  </div>
                </div>
              )}
              {/* Video badge */}
              {isVideo && (
                <div className="absolute top-2 left-2 px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded uppercase tracking-wider">
                  Video
                </div>
              )}
              {showCaptions && (m.title || m.caption) && (
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent text-white p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  {m.title && <div className="font-semibold text-sm">{m.title}</div>}
                  {m.caption && (
                    <div
                      className="text-xs text-white/80 line-clamp-2"
                      dangerouslySetInnerHTML={{ __html: m.caption }}
                    />
                  )}
                </figcaption>
              )}
            </figure>
          );
        })}
      </div>
    </div>
  );
};

export const THEMES = {
  grid: GridTheme,
  masonry: MasonryTheme,
  polaroid: PolaroidTheme,
  slideshow: SlideshowTheme,
  kenburns: KenburnsTheme,
  carousel: CarouselTheme,
  mosaic: MosaicTheme,
  justified: JustifiedTheme,
  timeline: TimelineTheme,
  filmstrip: FilmstripTheme,
  spotlight: SpotlightTheme,
  scrapbook: ScrapbookTheme,
  cube3d: Cube3DTheme,
  coverflow: CoverflowTheme,
  flipcards: FlipcardsTheme,
  marquee: MarqueeTheme,
  kinetic: KineticTheme,
  parallax: ParallaxTheme,
  immersive: ImmersiveTheme,
  skyscraper: SkyscraperTheme,
  vinyl: VinylTheme,
  carousel3d: Carousel3DTheme,
  filmreel: FilmReelTheme,
  videowall: VideoWallTheme,
};
